import { useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import { components, OptionProps } from "react-select";
import { shuffleArray } from "common/utils/arrays";
import { ROLES_TO_TEXT } from "common/utils/users";
import {
  useSearchPublicUsersLazyQuery,
  useSearchPublicUsersQuery,
  useSearchUsersLazyQuery,
  useSearchUsersQuery,
} from "../../generated/graphql";
import { Body } from "../Common/Typography";
import { OptionContainer } from "../Inputs/__styles__/MultiSelect";
import MultiSelect, { BaseOption } from "../Inputs/MultiSelect";
import { OptionHeader } from "./__styles__/UserSelect";

export interface UserOption extends BaseOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: { name: string };
  _userType?: "user" | "guest";
}

export type EntitySelectionOutput = Record<string, string[]>;

const UserOptionComponent = (
  option: UserOption,
  props: OptionProps<UserOption, true>,
) => {
  return (
    <components.Option {...props}>
      <OptionContainer>
        <OptionHeader>
          <Body size="large" type="regular" color="contentPrimary">
            {`${option.firstName} ${option.lastName}`}
          </Body>
          {option?.role && (
            <Body size="large" type="regular" color="contentSecondary">
              {ROLES_TO_TEXT[option.role.name as keyof typeof ROLES_TO_TEXT] ||
                option.role.name}
            </Body>
          )}
        </OptionHeader>
        <Body size="large" type="regular" color="contentSecondary">
          {option.email}
        </Body>
      </OptionContainer>
    </components.Option>
  );
};

const formatUserLabel = (option: UserOption) => {
  return `${option.firstName} ${option.lastName}`;
};

export type UserSelectProps = {
  name: string;
  randomizeOptionOrder?: boolean;
  value: EntitySelectionOutput;
  onChange: (value: Maybe<EntitySelectionOutput>) => void;
  /** Callback that provides full user objects when selection changes */
  onUserObjectsChange?: (users: UserOption[]) => void;
  disabled?: boolean;
  tooltip?: string;
  helperText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  includeUsers?: boolean;
  includePublicUsers?: boolean;
  showSelectAll?: boolean;
  userGroups?: string[];
};

const UserSelect = ({
  randomizeOptionOrder = false,
  name,
  value,
  onChange,
  onUserObjectsChange,
  disabled,
  tooltip,
  helperText,
  placeholder,
  label,
  required,
  error,
  includeUsers = true,
  includePublicUsers = false,
  showSelectAll = true,
  userGroups = [],
  ...props
}: UserSelectProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);

  const { data: usersData, loading: usersLoading } = useSearchUsersQuery({
    variables: { query: searchTerm },
    skip: !includeUsers,
  });

  const { data: publicUsersData, loading: publicUsersLoading } =
    useSearchPublicUsersQuery({
      variables: {
        query: searchTerm,
        userGroups: userGroups,
      },
      skip: !includePublicUsers,
    });

  const loading = usersLoading || publicUsersLoading;
  const initialValueRef = useRef(value);
  const [loadInitialUsers] = useSearchUsersLazyQuery();
  const [loadInitialPublicUsers] = useSearchPublicUsersLazyQuery();

  const sortUsers = (users: UserOption[]) => {
    return users.sort((a, b) => {
      const firstNameA = a.firstName?.toLowerCase() || "";
      const firstNameB = b.firstName?.toLowerCase() || "";
      return firstNameA.localeCompare(firstNameB);
    });
  };

  // On mount, query for selected users if value is set since we might need to load suspended users.
  useEffect(() => {
    const userIds = initialValueRef.current["users"] || [];
    const publicUserIds = initialValueRef.current["publicUsers"] || [];

    const loadAllUsers = async () => {
      let allInitialUsers: UserOption[] = [];

      if (userIds.length > 0) {
        const initialUsers = await loadInitialUsers({
          variables: { userIds: userIds },
        });

        if (initialUsers.data?.searchUsers) {
          const formattedUsers = initialUsers.data.searchUsers.map(user => ({
            ...user,
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
            _userType: "user" as const,
          }));
          allInitialUsers.push(...formattedUsers);
        }
      }

      if (publicUserIds.length > 0) {
        const initialPublicUsers = await loadInitialPublicUsers({
          variables: {
            publicUserIds: publicUserIds,
          },
        });

        if (initialPublicUsers.data?.searchPublicUsers) {
          const formattedPublicUsers =
            initialPublicUsers.data.searchPublicUsers.map(publicUser => ({
              ...publicUser,
              value: publicUser.id,
              label: `${publicUser.firstName} ${publicUser.lastName}`,
              role: { name: "Guest" },
              _userType: "guest" as const,
            }));

          allInitialUsers.push(...formattedPublicUsers);
        }
      }

      if (allInitialUsers.length > 0) {
        setSelectedUsers(sortUsers(allInitialUsers));
      }
    };

    void loadAllUsers();
  }, [loadInitialUsers, loadInitialPublicUsers]);

  const options = useMemo(() => {
    const allUsers: UserOption[] = [];

    if (usersData && includeUsers) {
      const formattedUsers = usersData.searchUsers.map(user => ({
        ...user,
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
        _userType: "user" as const,
      }));
      allUsers.push(...formattedUsers);
    }

    if (publicUsersData && includePublicUsers) {
      const formattedPublicUsers = publicUsersData.searchPublicUsers.map(
        publicUser => {
          const roleName = publicUser.userGroups?.[0]?.name || "Guest";

          return {
            id: publicUser.id,
            firstName: publicUser.firstName,
            lastName: publicUser.lastName,
            email: publicUser.email,
            value: publicUser.id,
            label: `${publicUser.firstName} ${publicUser.lastName}`,
            role: { name: roleName },
            _userType: "guest" as const,
          } as UserOption;
        },
      );

      allUsers.push(...formattedPublicUsers);
    }

    if (randomizeOptionOrder) {
      return shuffleArray(allUsers);
    }

    return sortUsers(allUsers);
  }, [
    usersData,
    publicUsersData,
    includeUsers,
    includePublicUsers,
    randomizeOptionOrder,
  ]);

  const handleInputChange = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleChange = (selectedValues: string[] | UserOption[] | null) => {
    if (!selectedValues) {
      setSelectedUsers([]);
      onChange(null);
      onUserObjectsChange?.([]);
      return;
    }

    const userOptions = selectedValues as UserOption[];
    setSelectedUsers(userOptions);

    const users = userOptions.filter(user => user._userType !== "guest");
    const publicUsers = userOptions.filter(user => user._userType === "guest");

    const newValue = { ...value };
    newValue["users"] = users.map(user => user.id);
    newValue["publicUsers"] = publicUsers.map(publicUser => publicUser.id);

    onChange(newValue);
    onUserObjectsChange?.(userOptions);
  };

  // Convert UserOption objects to string array for MultiSelect
  const selectedUserIds = selectedUsers.map(user => user.id);

  const customComputeValue = (value: string[]) => {
    return value
      .map(val => selectedUsers.find(opt => opt.value === val))
      .filter(Boolean) as UserOption[];
  };

  return (
    <MultiSelect
      name={name}
      value={selectedUserIds}
      onChange={handleChange}
      options={options}
      disabled={disabled}
      tooltip={tooltip}
      helperText={helperText}
      placeholder={placeholder}
      label={label}
      required={required}
      error={error}
      loading={loading}
      onInputChange={handleInputChange}
      renderOption={UserOptionComponent}
      formatOptionLabel={formatUserLabel}
      filterOption={() => true}
      useBaseOption={false}
      customComputeValue={customComputeValue}
      showSelectAll={showSelectAll}
      {...props}
    />
  );
};

export default UserSelect;

type ReactHookFormUserSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Pick<ControllerProps<TFieldValues, TName>, "control" | "name" | "rules"> &
  Omit<UserSelectProps, "onChange" | "value"> & {
    onChange?: (value: Maybe<EntitySelectionOutput>) => void;
  };

export const ReactHookFormUserSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  required = false,
  onChange,
  label,
  tooltip,
  helperText,
  disabled,
  placeholder,
  includeUsers = true,
  includePublicUsers = false,
  userGroups = [],
  showSelectAll = true,
  ...props
}: ReactHookFormUserSelectProps<TFieldValues, TName>) => {
  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <UserSelect
          name={name}
          value={field.value || { users: [], publicUsers: [] }}
          onChange={value => {
            field.onChange(value);
            onChange?.(value);
          }}
          error={fieldState.error?.message}
          required={required}
          label={label}
          tooltip={tooltip}
          helperText={helperText}
          disabled={disabled}
          placeholder={placeholder}
          includeUsers={includeUsers}
          includePublicUsers={includePublicUsers}
          userGroups={userGroups}
          showSelectAll={showSelectAll}
          {...props}
        />
      )}
    />
  );
};
